import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Button } from '@/common/components/Button/Button';
import { CardAction } from '@/common/components/CardAction/CardAction';
import { CardActionGroup } from '@/common/components/CardActionGroup/CardActionGroup';
import { Checkbox } from '@/common/components/Checkbox/Checkbox';
import { Icon } from '@/common/components/Icons/Icon';
import { ModalMultiStep } from '@/common/components/ModalMultiStep/ModalMultiStep';
import { Stack } from '@/common/components/Stack/Stack';
import { Translate } from '@/common/components/Translate/Translate';
import { useEmployeeDetails } from '@/common/contexts/user/UserDetailsContext';
import { useRemoveFromPolicy } from '@/common/integrations/menhir/employee/insurance/insurance.hooks';
import { IInsuranceMember } from '@/common/integrations/menhir/employee/insurance/insurance.types';
import { formatDate, now } from '@/common/utils/date';
import { notify } from '@/common/utils/toast/toast';
import { MoneyUtils } from '@coverflex-tech/hypernova-utils';
import { endOfMonth } from 'date-fns';

export type TInsuranceOptOutModalProps = {
  onClose: () => void;
  peopleToRemove: IInsuranceMember[];
  policyId: string;
  renewalDate: string;
  companyName: string;
};

export const InsuranceOptOutModal = ({
  onClose,
  peopleToRemove,
  policyId,
  renewalDate,
}: TInsuranceOptOutModalProps) => {
  const { t } = useTranslation();
  const [checked, setChecked] = useState(false);
  const { mutateAsync, isLoading } = useRemoveFromPolicy();
  const { company } = useEmployeeDetails();

  const onConfirm = async () => {
    try {
      await mutateAsync({
        policyId: policyId,
      });

      notify({
        type: 'success',
        message: t(
          'dashboard.insurance_detail.opt_out_modal.confirm.sucess_message'
        ),
      });
    } catch (error) {}
  };

  const isFamilyMemberExclusion =
    peopleToRemove.length === 1 && peopleToRemove[0]?.type !== 'employee';

  const cardsList = (
    <CardActionGroup size="sm" plain>
      {peopleToRemove.map((person) => (
        <CardAction
          title={person.name}
          key={person.id}
          description={
            person.cost ? (
              <>
                <Stack>
                  {person.cost.paidByCompany &&
                    person.cost.paidByCompany.amount !== 0 && (
                      <BodyText size="sm" color="neutral20">
                        {t(
                          'dashboard.insurance_detail.opt_out_modal.paid_by_company.card_description',
                          {
                            value: MoneyUtils.moneyToString(
                              person.cost?.paidByCompany
                            ),
                          }
                        )}
                      </BodyText>
                    )}
                  {person.cost.paidByEmployee &&
                    person.cost.paidByEmployee.amount !== 0 && (
                      <BodyText size="sm" color="neutral20">
                        {t(
                          'dashboard.insurance_detail.opt_out_modal.paid_by_employee.card_description',
                          {
                            value: MoneyUtils.moneyToString(
                              person.cost?.paidByEmployee
                            ),
                          }
                        )}
                      </BodyText>
                    )}
                </Stack>
              </>
            ) : null
          }
          icon={<Icon icon="user" />}
          iconBadgeColor="neutral30"
        />
      ))}
    </CardActionGroup>
  );

  return (
    <ModalMultiStep
      icon={<Icon icon="shield-slash" />}
      iconBadgeColor="accentSecondary20"
      title={t('dashboard.insurance_detail.opt_out_modal.title')}
      steps={[
        {
          content: (
            <Stack gap="lg">
              <BodyText>
                {t('dashboard.insurance_detail.opt_out_modal.description')}
              </BodyText>
              {cardsList}
            </Stack>
          ),
          actions: ({ onNext }) => (
            <Button variant="primary" onPress={onNext}>
              {t('dashboard.insurance_detail.opt_out_modal.next_button')}
            </Button>
          ),
        },
        {
          content: (
            <Stack key="secondStep" gap="md">
              {isFamilyMemberExclusion ? (
                <>
                  <BodyText>
                    {t(
                      'dashboard.insurance_detail.family_member.opt_out_modal.email.info'
                    )}
                  </BodyText>
                  <Translate
                    i18nKey="dashboard.insurance_detail.family_member.opt_out_modal.renewal_date.info"
                    values={{ renewalDate: renewalDate }}
                    parent={BodyText}
                  />
                  <Translate
                    i18nKey="dashboard.insurance_detail.family_mmeber.opt_out_modal.ends_on.info"
                    values={{
                      endsOn: formatDate(endOfMonth(now())),
                      name: peopleToRemove[0]?.name,
                    }}
                    parent={BodyText}
                  />
                </>
              ) : (
                <>
                  <Translate
                    i18nKey="dashboard.insurance_detail.opt_out_modal.ends_on.info"
                    values={{ endsOn: formatDate(endOfMonth(now())) }}
                    parent={BodyText}
                  />
                  <BodyText>
                    {t('dashboard.insurance_detail.opt_out_modal.email.info')}
                  </BodyText>
                  <Translate
                    i18nKey="dashboard.insurance_detail.opt_out_modal.renewal_date.info"
                    values={{ renewalDate: renewalDate }}
                    parent={BodyText}
                  />
                  <Checkbox
                    checked={checked}
                    label={t(
                      'dashboard.insurance_detail.opt_out_modal.checkbox.label'
                    )}
                    description={t(
                      'dashboard.insurance_detail.opt_out_modal.checkbox.description',
                      { company_name: company.name }
                    )}
                    onChange={(checked) => {
                      setChecked(checked);
                    }}
                  />
                </>
              )}
            </Stack>
          ),
          actions: ({ onBack, closeModal }) => (
            <Fragment key="actions">
              <Button variant="tertiary" onPress={onBack} disabled={isLoading}>
                {t('general.navigation.back')}
              </Button>
              <Button
                variant="primary"
                onPress={() => {
                  onConfirm();
                  closeModal();
                }}
                disabled={isLoading || (!checked && !isFamilyMemberExclusion)}
              >
                {t('dashboard.insurance_detail.opt_out_modal.confirm')}
              </Button>
            </Fragment>
          ),
        },
      ]}
      heightClass="h-[60rem]"
      onClose={onClose}
    />
  );
};
